import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, merge, Observable, of } from 'rxjs';
import { catchError, map, mergeMap, shareReplay, switchMap } from 'rxjs/operators';
import { BriefSubjectDetailContract } from '../../contracts/brief-subject-detail.contract';
import { LicenseDatesContract } from '../../contracts/license-dates.contract';
import { SubjectLicensesContract } from '../../contracts/subject-licenses.contract';
import { LicenseDataSource } from '../../enums/license-data-source.enum';
import { LicenseGeneralFeature } from '../../enums/license-general-feature.enum';
import { LicenseManagementFeature } from '../../enums/license-management-feature.enum';
import { LicensePapFeature } from '../../enums/license-pap-feature.enum';
import { LicenseProductType } from '../../enums/license-product-type.enum';
import { LicenseState } from '../../enums/license-state.enum';
import { IsophiException } from '../../exceptions/isophi.exception';
import { AuthService } from './auth.service';
import { HttpService } from './http.service';
import { IsophiCoreService } from './isophi-core.service';
import { LicenseChildPortfolioFeatureEnum } from '../../enums';

export interface LicenseRetrieve {
  validFrom: Date | null;
  validTo: Date | null;
  daysUntilEnd: number | null;
  active: boolean;
}

/**
 * Manage licenses in FE apps.
 *
 * It communicates with BE license server and checks client licenses.
 */
@Injectable({
  providedIn: 'root',
})
export class LicenseService {
  private static readonly CACHE_TIME_IN_HOURS = 24;

  /**
   * Set source of license data.
   */
  public licenseDataSource: LicenseDataSource = LicenseDataSource.API;

  /**
   * Subject (kindergarten/user/...) UUID.
   * All license requests are associated with this subject.
   *
   * When data source is LOCAL_DATA, you don't have to set this value.
   * But library client has to ensure that passed license data are associated
   * with correct subject.
   */
  public licenseSubjectUuid: string | null = null;

  /**
   * License data passed by library client.
   * This field is used when licenseDataSource == LOCAL_DATA
   */
  public data: SubjectLicensesContract | null = null;

  /**
   * Cached data from License service response.
   * This field is used when licenseDataSource == API
   *
   * Data are cached for CACHE_TIME_IN_HOURS h, after that are refreshed.
   */
  private cache$: Observable<SubjectLicensesContract> | null = null;

  /**
   * Observable that triggers licenses to reload when emitted
   */
  private readonly licensesReload$ = new BehaviorSubject<void>(null);

  private cacheFilledDate: Date | null = null;

  private readonly httpService = inject(HttpService);
  private readonly httpClient = inject(HttpClient);
  private readonly isophiCoreService = inject(IsophiCoreService);
  private readonly authService = inject(AuthService);

  // ---------------------------------------------------------------------------
  // Core public API - generic license checks
  // ---------------------------------------------------------------------------

  /**
   * Check that subject has product with valid specified feature.
   */
  public hasProductWithFeature(productCodename: string, featureCodename: string): Observable<boolean> {
    return this.licenseData.pipe(
      map((data) => {
        const licenseDates = data.licenses?.[productCodename]?.[featureCodename];
        if (licenseDates === undefined) return false;
        return this.isLicenseValidNow(licenseDates);
      })
    );
  }

  /**
   * Check that subject has product with any feature.
   */
  public hasProduct(productCodename: string, anyFeatureActive = true): Observable<boolean> {
    return this.licenseData.pipe(
      map((data) => {
        const product = data.licenses?.[productCodename];
        if (product === undefined) return false;
        if (!anyFeatureActive) return true;
        for (const feature of Object.keys(product)) {
          const licenseDates = product[feature];
          if (this.isLicenseValidNow(licenseDates)) return true;
        }
        return false;
      })
    );
  }

  /**
   * Check that subject has any product of specified type.
   */
  public hasAnyProduct(productType: LicenseProductType, anyFeatureActive = true): Observable<boolean> {
    return this.getProductsByType(productType).pipe(
      mergeMap((productCodenames) => {
        if (productCodenames.length === 0) return of([false]); // No products => resolve to false
        return combineLatest(productCodenames.map((productCodename) => this.hasProduct(productCodename, anyFeatureActive)));
      }),
      map((productChecks) => productChecks.some((v) => v))
    );
  }

  /**
   * Check that subject has all products with any feature.
   */
  public hasProducts(productCodenames: string[], anyFeatureActive = true): Observable<boolean> {
    return combineLatest(productCodenames.map((productCodename) => this.hasProduct(productCodename, anyFeatureActive))).pipe(
      map((productChecks) => productChecks.every((v) => v))
    );
  }

  /**
   * Check that subject has multiple products (all of them) with specified feature.
   */
  public hasProductsWithFeature(productCodenames: string[], featureCodename: string, featureActive = true): Observable<boolean> {
    return this.licenseData.pipe(
      map((data) => {
        for (const productCodename of productCodenames) {
          const licenseDates = data.licenses?.[productCodename]?.[featureCodename];
          if (licenseDates === undefined) return false;
          if (featureActive && !this.isLicenseValidNow(licenseDates)) return false;
        }
        return true;
      })
    );
  }

  /**
   * Check that subject has any product of specified type with specified feature.
   */
  public hasAnyProductWithFeature(
    productType: LicenseProductType,
    featureCodename: string,
    featureActive = true
  ): Observable<boolean> {
    return combineLatest({
      products: this.getProductsByType(productType),
      licenses: this.licenseData.pipe(map((data) => data.licenses)),
    }).pipe(
      map((data) => {
        for (const productCodename of data.products) {
          const licenseDates = data.licenses?.[productCodename]?.[featureCodename];
          if (licenseDates === undefined) continue;
          if (featureActive && !this.isLicenseValidNow(licenseDates)) continue;
          return true;
        }
        return false;
      })
    );
  }

  /**
   * Check whether module should be locked.
   * Module stays unlocked when there is at least one active access license.
   */
  public isModuleLocked(
    productType: LicenseProductType,
    accessFeatureCodename: string,
    trialFeatureCodename: string
  ): Observable<boolean> {
    return combineLatest([
      this.hasAnyProductWithFeature(productType, accessFeatureCodename),
      this.hasAnyProductWithFeatureState(productType, accessFeatureCodename, LicenseState.EXPIRED),
      this.hasAnyProductWithFeatureState(productType, trialFeatureCodename, LicenseState.EXPIRED),
    ]).pipe(
      map(([hasActiveAccess, hasExpiredAccess, hasExpiredTrial]) => !hasActiveAccess && (hasExpiredAccess || hasExpiredTrial))
    );
  }

  /**
   * Check that subject has product with specified feature that should be visible in UI.
   * Visible when license exists and is active or expired.
   */
  public hasProductWithFeatureVisible(productCodename: string, featureCodename: string): Observable<boolean> {
    return this.licenseData.pipe(
      map((data) => {
        const licenseDates = data.licenses?.[productCodename]?.[featureCodename];
        const state = this.getLicenseStateFromDates(licenseDates);
        return this.isLicenseStateVisible(state);
      })
    );
  }

  /**
   * Check that subject has any product of specified type with specified feature that should be visible in UI.
   * Visible when license exists and is active or expired.
   */
  public hasAnyProductWithFeatureVisible(productType: LicenseProductType, featureCodename: string): Observable<boolean> {
    return combineLatest({
      products: this.getProductsByType(productType),
      licenses: this.licenseData.pipe(map((data) => data.licenses)),
    }).pipe(
      map((data) => {
        for (const productCodename of data.products) {
          const licenseDates = data.licenses?.[productCodename]?.[featureCodename];
          const state = this.getLicenseStateFromDates(licenseDates);
          if (this.isLicenseStateVisible(state)) return true;
        }
        return false;
      })
    );
  }

  /**
   * Returns normalized license state for a specific product and feature.
   */
  public getLicenseState(productCodename: string, featureCodename: string): Observable<LicenseState> {
    return this.licenseData.pipe(
      map((data) => {
        const licenseDates = data.licenses?.[productCodename]?.[featureCodename];
        return this.getLicenseStateFromDates(licenseDates);
      })
    );
  }

  /**
   * Returns detailed license status for a specific product and feature.
   */
  public getLicenseStatus(productCodename: string, featureCodename: string): Observable<LicenseRetrieve> {
    return this.licenseData.pipe(
      map((data) => {
        const licenseDates = data.licenses?.[productCodename]?.[featureCodename];

        if (!licenseDates) {
          return { validFrom: null, validTo: null, daysUntilEnd: 0, active: false };
        }

        const validFrom = licenseDates.valid_from ? new Date(licenseDates.valid_from) : null;
        const validTo = licenseDates.valid_to ? new Date(licenseDates.valid_to) : null;

        let daysUntilEnd: number | null = null;
        if (validTo) {
          daysUntilEnd = 0;
          const now = new Date();
          const diffMs = validTo.getTime() - now.getTime();
          daysUntilEnd = Math.max(0, Math.ceil(diffMs / (1000 * 60 * 60 * 24)));
        }

        return {
          validFrom,
          validTo,
          daysUntilEnd,
          active: this.isLicenseValidNow(licenseDates),
        };
      })
    );
  }

  /**
   * Return list of subject's products by product type.
   */
  public getProductsByType(productType: LicenseProductType): Observable<string[]> {
    return this.licenseData.pipe(
      map((data) => {
        const products = data.product_types?.[productType];
        if (products === undefined) return [];
        return products;
      })
    );
  }

  // ---------------------------------------------------------------------------
  // Public API - HTTP calls and cache control
  // ---------------------------------------------------------------------------

  public getBriefSubjectDetail(): Observable<BriefSubjectDetailContract> {
    const url = `${this.isophiCoreService.licenseApi}/license-subjects/${this.licenseSubjectUuid}/brief-detail/`;
    const options = this.httpService.createRequestOptions(this.authService.accessToken);
    return this.httpClient.get<BriefSubjectDetailContract>(url, options);
  }

  public applyCode(code: string): Observable<void> {
    const url = `${this.isophiCoreService.licenseApi}/activation-codes/apply/`;
    const options = this.httpService.createRequestOptions(this.authService.accessToken);
    const data = { activation_code: code };
    return this.httpClient.post<void>(url, data, options);
  }

  /**
   * Convenient getters for frequently used license checks
   */

  public get accessChildPortfolio(): Observable<boolean> {
    return this.hasAnyProductWithFeature(LicenseProductType.GENERAL, LicenseGeneralFeature.MENU_PORTFOLIO);
  }

  public callLicensesApi(childUuid: string): Observable<SubjectLicensesContract> {
    if (childUuid === null) {
      throw new IsophiException('You have to set "licenseSubjectUuid" to use license service.');
    }

    const url = `${this.isophiCoreService.licenseApi}/licenses/${childUuid}/`;
    const options = this.httpService.createRequestOptions(this.authService.accessToken);
    return this.httpClient.get<SubjectLicensesContract>(url, options).pipe(
      shareReplay(1),
      catchError((error) => {
        console.error(error);
        throw new Error('Unable to download communication license data.');
      })
    );
  }

  public reloadLicenses(): void {
    this.licensesReload$.next();
  }

  public clear(): void {
    this.cache$ = null;
  }

  // ---------------------------------------------------------------------------
  // Convenient getters for frequently used license checks
  // ---------------------------------------------------------------------------

  // General
  public get canSeeDemoKindergarten(): Observable<boolean> {
    return this.hasAnyProductWithFeatureVisible(LicenseProductType.GENERAL, LicenseGeneralFeature.SHOWCASE);
  }

  public get canSeeTestingProduct(): Observable<boolean> {
    return this.hasAnyProductWithFeatureVisible(LicenseProductType.GENERAL, LicenseGeneralFeature.TESTING);
  }

  public get canUseTransitionReport(): Observable<boolean> {
    return this.hasAnyProductWithFeature(LicenseProductType.GENERAL, LicenseGeneralFeature.REPORT_TRANSITION);
  }

  public get internalTesting(): Observable<boolean> {
    return this.hasAnyProductWithFeature(LicenseProductType.GENERAL, LicenseGeneralFeature.TESTING);
  }

  // Child portfolio

  public get canUseChildrenPortfolioExtraMaterials(): Observable<boolean> {
    return this.hasAnyProductWithFeature(LicenseProductType.CHILD_PORTFOLIO, LicenseChildPortfolioFeatureEnum.EXTRA_MATERIALS);
  }

  /** ZS */
  public get isSchoolKindergarten(): Observable<boolean> {
    return this.hasAnyProductWithFeature(LicenseProductType.GENERAL, LicenseGeneralFeature.DASHBOARD_SCHOOL);
  }

  /** SK */
  public get isSKKindergarten(): Observable<boolean> {
    return this.hasAnyProductWithFeature(LicenseProductType.GENERAL, LicenseGeneralFeature.DASHBOARD_SK);
  }

  /** Check if any diagnostic test has test_kit (physical product) */
  public get hasAnyTestKit(): Observable<boolean> {
    return combineLatest([
      this.hasProductWithFeature(LicenseProductType.TEST_3_4, LicenseGeneralFeature.TEST_KIT),
      this.hasProductWithFeature(LicenseProductType.TEST_4_5, LicenseGeneralFeature.TEST_KIT),
      this.hasProductWithFeature(LicenseProductType.TEST_5_7_v2, LicenseGeneralFeature.TEST_KIT),
      this.hasProductWithFeature(LicenseProductType.TEST_Z, LicenseGeneralFeature.TEST_KIT),
    ]).pipe(map((testKits) => testKits.some((val) => !!val)));
  }

  /** Check if user ahs any trial feature */
  public get hasAnyTrial(): Observable<boolean> {
    return combineLatest([
      this.hasProductWithFeature(LicenseProductType.MANAGEMENT, LicenseGeneralFeature.TRIAL),
      this.hasProductWithFeature(LicenseProductType.CHILD_PORTFOLIO, LicenseGeneralFeature.TRIAL),
      this.hasProductWithFeature(LicenseProductType.ANALYTICS, LicenseGeneralFeature.TRIAL),
      this.hasProductWithFeature(LicenseProductType.PAP, LicenseGeneralFeature.TRIAL),
    ]).pipe(map((testKits) => testKits.some((val) => !!val)));
  }

  // Management
  public get accessManagement(): Observable<boolean> {
    return this.hasAnyProductWithFeature(LicenseProductType.MANAGEMENT, LicenseManagementFeature.ACCESS_MANAGEMENT);
  }

  public get hasTrialManagement(): Observable<boolean> {
    return this.hasAnyProductWithFeature(LicenseProductType.MANAGEMENT, LicenseManagementFeature.TRIAL);
  }

  public get canSeeManagement(): Observable<boolean> {
    return this.hasAnyProductWithFeatureVisible(LicenseProductType.GENERAL, LicenseGeneralFeature.MENU_MANAGEMENT);
  }

  public get canSeeChildPortfolio(): Observable<boolean> {
    return this.hasAnyProductWithFeatureVisible(LicenseProductType.GENERAL, LicenseGeneralFeature.MENU_PORTFOLIO);
  }

  // PAP
  public get accessPap(): Observable<boolean> {
    return this.hasAnyProductWithFeature(LicenseProductType.PAP, LicensePapFeature.ACCESS_PAP);
  }

  public get hasTrialPap(): Observable<boolean> {
    return this.hasAnyProductWithFeature(LicenseProductType.PAP, LicensePapFeature.TRIAL);
  }

  public get canSeePap(): Observable<boolean> {
    return this.hasAnyProductWithFeatureVisible(LicenseProductType.GENERAL, LicenseGeneralFeature.TEACHER_GROWTH);
  }

  // T-APP
  public get canSeeTeacherApp(): Observable<boolean> {
    return this.hasAnyProductWithFeatureVisible(LicenseProductType.GENERAL, LicenseGeneralFeature.MENU_DIAGNOSTICS);
  }


  // ---------------------------------------------------------------------------
  // Private helpers - data access & cache
  // ---------------------------------------------------------------------------

  /**
   * Return licenses data in server format.
   */
  private get licenseData(): Observable<SubjectLicensesContract> {
    if (this.licenseDataSource === LicenseDataSource.LOCAL_DATA) return of(this.data);

    if (this.isCacheInvalid()) this.initData();
    return this.cache$;
  }

  /**
   * Check if cache is invalid.
   *
   * Cache is invalid before first data download or when data are CACHE_TIME_IN_HOURS hours old.
   */
  private isCacheInvalid(): boolean {
    const now = new Date();
    const cacheValidTo = new Date(this.cacheFilledDate);
    cacheValidTo.setHours(cacheValidTo.getHours() + LicenseService.CACHE_TIME_IN_HOURS);
    return this.cache$ === null || cacheValidTo < now;
  }

  /**
   * Init cache observable.
   */
  private initData(): void {
    if (this.licenseSubjectUuid === null) {
      throw new IsophiException('You have to set "licenseSubjectUuid" to use license service.');
    }

    const url = `${this.isophiCoreService.licenseApi}/licenses/${this.licenseSubjectUuid}/`;
    const options = this.httpService.createRequestOptions(this.authService.accessToken);
    this.cacheFilledDate = new Date();

    if (!this.cache$) {
      this.cache$ = merge(of(0), this.licensesReload$).pipe(
        switchMap(() => this.httpClient.get<SubjectLicensesContract>(url, options)),
        shareReplay(1),
        catchError((error) => {
          console.error(error);
          throw new Error('Unable to download license data.');
        })
      );
    } else {
      this.reloadLicenses();
    }
  }

  /**
   * Check if one license dates are valid and license is active.
   */
  private isLicenseValidNow(licenseDates: LicenseDatesContract): boolean {
    const now = new Date();

    const validFrom = licenseDates.valid_from ? new Date(licenseDates.valid_from) : null;
    const validTo = licenseDates.valid_to ? new Date(licenseDates.valid_to) : null;
    const tolerationTo = licenseDates.toleration_to ? new Date(licenseDates.toleration_to) : null;

    // Normalize dates. This is to ensure e.g. when validFrom is in the same day as now to treat it as valid
    if (validFrom) {
      validFrom.setHours(0, 0, 0, 0);
    }
    if (validTo) {
      validTo.setHours(23, 59, 59, 0);
    }
    if (tolerationTo) {
      tolerationTo.setHours(23, 59, 59, 0);
    }

    const realEnd = tolerationTo ?? validTo;

    if (realEnd && realEnd < now) return false;
    return !(validFrom && validFrom > now);
  }

  /**
   * Normalize license dates to a stable state.
   */
  private getLicenseStateFromDates(licenseDates?: LicenseDatesContract | null): LicenseState {
    if (!licenseDates) return LicenseState.NOT_OWNED;

    const now = new Date();
    const validFrom = licenseDates.valid_from ? new Date(licenseDates.valid_from) : null;
    const validTo = licenseDates.valid_to ? new Date(licenseDates.valid_to) : null;
    const tolerationTo = licenseDates.toleration_to ? new Date(licenseDates.toleration_to) : null;
    const realEnd = tolerationTo ?? validTo;

    if (validFrom && validFrom > now) return LicenseState.FUTURE;
    if (realEnd && realEnd < now) return LicenseState.EXPIRED;
    return LicenseState.ACTIVE;
  }

  private isLicenseStateVisible(state: LicenseState): boolean {
    return state === LicenseState.ACTIVE || state === LicenseState.EXPIRED;
  }

  private hasAnyProductWithFeatureState(
    productType: LicenseProductType,
    featureCodename: string,
    stateToMatch: LicenseState
  ): Observable<boolean> {
    return combineLatest({
      products: this.getProductsByType(productType),
      licenses: this.licenseData.pipe(map((data) => data.licenses)),
    }).pipe(
      map((data) => {
        for (const productCodename of data.products) {
          const licenseDates = data.licenses?.[productCodename]?.[featureCodename];
          const state = this.getLicenseStateFromDates(licenseDates);
          if (state === stateToMatch) return true;
        }
        return false;
      })
    );
  }
}
